import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  People,
  ContentCopy,
  Visibility,
  VpnKey,
  BarChart,
  CreditCard,
  CheckCircle,
  Warning,
  Chat as ChatIcon,
  Business,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { adminAPI } from '../services/api';
import { ROOT_COMPANY_UUID } from '../helpers/constants';
import {
  getTableHeaderStyles,
  getTableContainerStyles,
  getTableStyles,
  getTableCellStyles,
  getTableRowStyles,
  formatStatusText,
} from '../styles/tableStyles';
import { formatTableDate } from '../utils/dateUtils';
import { ApiKey } from '../types';
import { Company } from '../hooks/useCompanies';
import Modal from '../components/Modal';
import ApiKeyDialog from '../components/ApiKeyDialog';

interface User {
  name?: string;
  email: string;
  role: string;
}

const CustomerDetails: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();

  // Pagination state for users table inside accordion
  const [usersPage, setUsersPage] = React.useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = React.useState(10);

  const [customerData, setCustomerData] = React.useState<Company | null>(null);
  const [firstAdminEmail, setFirstAdminEmail] = React.useState<string | null>(null);
  const [companyUsers, setCompanyUsers] = React.useState<User[]>([]);
  const [apiKeys, setApiKeys] = React.useState<ApiKey[]>([]);
  const [usageData, setUsageData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [showApiKeyDialog, setShowApiKeyDialog] = React.useState(false);
  const [selectedApiKey, setSelectedApiKey] = React.useState<ApiKey | null>(null);
  const [fullApiKey, setFullApiKey] = React.useState<string>('');
  const [loadingFullKey, setLoadingFullKey] = React.useState(false);

  React.useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customerId) {
        setError('Customer ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch customer details, first admin email, company users, API keys, and usage data
        const [customerResponse, adminUserResponse, companyUsersResponse, apiKeysResponse, usageResponse] = await Promise.all([
          adminAPI.getCompany(customerId),
          adminAPI.getCompanyAdminUser(customerId).catch(() => ({ data: { email: null } })),
          adminAPI.getCompanyUsers(customerId).catch(() => ({ data: [] })),
          adminAPI.getApiKeysByCompany(customerId),
          adminAPI.getCompanyUsage(customerId).catch(() => ({ data: [] })), // Handle case where usage data might not exist
        ]);

        setCustomerData(customerResponse.data);
        setFirstAdminEmail(adminUserResponse.data?.email ?? null);
        setCompanyUsers(companyUsersResponse.data ?? []);
        setApiKeys(apiKeysResponse.data);
        setUsageData(usageResponse.data);
      } catch (err: any) {
        console.error('Error fetching customer details:', err);
        setError(err.message || 'Failed to fetch customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId]);

  // Reset users table pagination when switching to another customer
  React.useEffect(() => {
    setUsersPage(0);
  }, [customerId]);

  const handleBack = () => {
    navigate('/customers');
  };

  const handleCopyToken = async (keyId: string) => {
    try {
      const response = await adminAPI.getApiKey(keyId);
      const fullKey = response.data.fullToken || response.data.tokenPrefix || '';
      navigator.clipboard.writeText(fullKey);
      showToast('API key copied to clipboard!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to copy API key', 'error');
    }
  };

  const handleShowFullApiKey = async (key: ApiKey) => {
    try {
      setLoadingFullKey(true);
      setSelectedApiKey(key);
      setShowApiKeyDialog(true);
      const response = await adminAPI.getApiKey(key.id);
      setFullApiKey(response.data.fullToken || response.data.tokenPrefix || '');
    } catch (err: any) {
      showToast(err.message || 'Failed to fetch full API key', 'error');
      setShowApiKeyDialog(false);
    } finally {
      setLoadingFullKey(false);
    }
  };

  const handleGenerateNewToken = async () => {
    try {
      if (!customerId) {
        showToast('Customer ID is required', 'error');
        return;
      }
      await adminAPI.generateApiKey({
        company_id: customerId,
        name: 'Default Token',
        description: 'Auto-generated token',
        is_active: true,
      });
      showToast('New API key generated successfully!', 'success');
      const apiKeysResponse = await adminAPI.getApiKeysByCompany(customerId);
      setApiKeys(apiKeysResponse.data);
    } catch (err: any) {
      showToast(err.message || 'Failed to generate API key', 'error');
    }
  };

  // Pagination handlers for users table
  const handleUsersPageChange = (_event: unknown, newPage: number) => {
    setUsersPage(newPage);
  };
  const handleUsersRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsersRowsPerPage(parseInt(event.target.value, 10));
    setUsersPage(0);
  };

  const handleDeleteCompany = async () => {
    if (!customerId) {
      showToast('Customer ID is required', 'error');
      return;
    }

    // Prevent deletion of root company (specific UUID: 00000000-0000-0000-0000-000000000001)
    if (customerData?.id === ROOT_COMPANY_UUID) {
      showToast('Cannot delete the root company. This is a protected system company.', 'error');
      return;
    }

    try {
      setDeleting(true);
      await adminAPI.deleteCompany(customerId);
      showToast('Company deleted successfully!', 'success');
      navigate('/customers');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete company', 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customerData) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            Back
          </Button>
        </Box>
        <Alert severity="error">
          {error || 'Failed to load customer details. Please try again.'}
        </Alert>
      </Box>
    );
  }

  // Get the primary API key (first active one or first one)
  const primaryApiKey = apiKeys.length > 0 ? (apiKeys.find(key => key.isActive) || apiKeys[0]) : null;

  // Primary contact: use company contactEmail, else company's first admin email
  const primaryContactEmail = customerData.contactEmail || firstAdminEmail || null;

  // Format customer data for display
  const displayData = {
    id: customerData.id,
    company: customerData.name,
    contact: primaryContactEmail || 'No contact email',
    domain: customerData.domain,
    industry: customerData.industry,
    status: customerData.status,
    created: formatTableDate(customerData.createdAt, 'created'),
    lastLogin: customerData.lastLoginAt ? formatTableDate(customerData.lastLoginAt, 'lastLogin') : 'Never',
    apiToken: primaryApiKey?.tokenPrefix || 'No API key available',
    plan: customerData.planName || 'No Plan',
    planId: customerData.planId,
    lastUsed: formatTableDate(primaryApiKey?.lastUsedAt, 'lastUsed'),
    mrr: customerData.planPrice ? `$${customerData.planPrice}` : 'N/A',
    usageToday: primaryApiKey?.usageCount || 0, // Real usage count
    apiCalls: { 
      used: usageData?.find((u: any) => u.resource_type === 'api_calls')?.total_usage || 0, 
      limit: customerData.planFeatures?.api_calls || 50000 
    },
    tokens: { 
      used: usageData?.find((u: any) => u.resource_type === 'tokens')?.total_usage || 0, 
      limit: customerData.planFeatures?.tokens || 1000000 
    },
    resetDate: formatTableDate(customerData.subscriptionEndDate, 'created'),
    nextBill: formatTableDate(customerData.nextBillingDate, 'created'),
    paymentValid: customerData.subscriptionStatus === 'active',
    // Invoice status: from Zoho last invoice (same as fetchCompanySubscriptionDetails) when present, else from subscription status
    invoiceStatusLabel: customerData.lastInvoiceStatus ? formatStatusText(customerData.lastInvoiceStatus) : customerData.subscriptionStatus === 'active' ? 'Valid' : customerData.subscriptionStatus === 'cancelled' ? 'Cancelled' : customerData.subscriptionStatus === 'past_due' ? 'Past due' : customerData.subscriptionStatus ? formatStatusText(customerData.subscriptionStatus) : 'N/A',
    // Icon: paid = success; overdue/other = warning when we have Zoho invoice status; else use subscription active
    invoiceStatusValid: customerData.lastInvoiceStatus ? customerData.lastInvoiceStatus === 'paid' : customerData.subscriptionStatus === 'active',
    // Real company users from API (name optional; when absent, derived from email for display)
    users: companyUsers.map((u) => ({
      name: u.name ?? undefined,
      email: u.email ?? '',
      role: u.role ? (u.role.charAt(0).toUpperCase() + u.role.slice(1).toLowerCase()) : 'User',
    })),
  };

  return (
    <Box sx={{ flexGrow: 1, py: 3, px: 0 }}>
      {/* Header: back button only */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        {/* Left column: Company details card */}
        <Grid item xs={12} md={4}>
          {/* Left card: minHeight so card has more vertical space */}
          <Paper sx={{ p: 3, minHeight: 420, height: 'fit-content', borderRadius: 2, overflow: 'hidden' }}>
            {/* Building icon and company name; name truncates, full name in tooltip on hover */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, minWidth: 0 }}>
              <Business sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
              <Tooltip title={displayData.company} placement="top" enterDelay={300}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayData.company}
                </Typography>
              </Tooltip>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Details: labels and values in two columns, left-aligned and vertically aligned */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 24 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                  Primary Contact
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left' }}>{displayData.contact}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 24 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                  Domain
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left' }}>{displayData.domain}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                  Status
                </Typography>
                <Chip label={formatStatusText(displayData.status)} color="success" size="small" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 24 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                  Created On
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left' }}>{displayData.created}</Typography>
              </Box>
            </Box>
            {/* Action buttons: Delete Company (and optional Edit) inside card */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
              {customerData?.id === ROOT_COMPANY_UUID ? (
                <Tooltip title="This is the root company and cannot be deleted. It is protected to maintain system integrity.">
                  <span>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      disabled
                      sx={{ textTransform: 'none' }}
                    >
                      Delete Company
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setShowDeleteModal(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Delete Company
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right column: accordion sections (Billing, Users, API Key, Usage, Chat Service) */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Billing accordion */}
            <Accordion defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Billing
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {/* Billing: two-column layout — label label / value value */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, columnGap: 3 }}>
                  {/* Column 1 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Current Plan</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', mb: 2 }}>{displayData.plan}</Typography>
                    <Typography variant="body2" color="text.secondary">Invoice Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      {displayData.invoiceStatusValid ? (
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                      )}
                      <Typography variant="body2">
                        {displayData.invoiceStatusLabel}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">Subscription Status</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={formatStatusText(customerData.subscriptionStatus || 'No subscription')}
                        color={customerData.subscriptionStatus === 'active' ? 'success' : customerData.subscriptionStatus === 'cancelled' ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  {/* Column 2 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Plan Cost</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'left', mb: 2 }}>{displayData.mrr}</Typography>
                    <Typography variant="body2" color="text.secondary">Next Invoice On</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', mb: 2 }}>{displayData.nextBill}</Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Users accordion: table view with same design as Customers / Subscription Plans */}
            <Accordion defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 1, mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <People sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Users ({displayData.users.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                <Paper sx={getTableContainerStyles()}>
                  <TableContainer>
                    <Table {...getTableStyles()}>
                      <TableHead>
                        <TableRow sx={getTableRowStyles()}>
                          <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                            Name
                          </TableCell>
                          <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                            Email
                          </TableCell>
                          <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                            Role
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.users.length === 0 ? (
                          <TableRow sx={getTableRowStyles()}>
                            <TableCell colSpan={3} align="center" sx={getTableCellStyles()}>
                              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                No users found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          displayData.users
                            .slice(
                              usersPage * usersRowsPerPage,
                              usersPage * usersRowsPerPage + usersRowsPerPage
                            )
                            .map((user: User, index: number) => (
                              <TableRow key={`${user.email}-${index}`} sx={getTableRowStyles()}>
                                <TableCell sx={getTableCellStyles()}>
                                  <Typography variant="body2">{user.name ?? user.email?.split('@')[0] ?? '—'}</Typography>
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Typography variant="body2">{user.email}</Typography>
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip
                                    label={formatStatusText(user.role)}
                                    color={user.role.toLowerCase() === 'admin' ? 'primary' : 'default'}
                                    size="small"
                                    sx={user.role.toLowerCase() === 'admin' ? { color: 'white', backgroundColor: 'primary.main' } : undefined}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={displayData.users.length}
                    rowsPerPage={usersRowsPerPage}
                    page={usersPage}
                    onPageChange={handleUsersPageChange}
                    onRowsPerPageChange={handleUsersRowsPerPageChange}
                  />
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* API Key accordion */}
            <Accordion defaultExpanded sx={{ display: 'none', boxShadow: 1, mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <VpnKey sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    API Key
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleGenerateNewToken(); }}
                  sx={{ textTransform: 'none', mr: 1 }}
                >
                  New
                </Button>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>API Key:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {primaryApiKey?.tokenPrefix && primaryApiKey.tokenPrefix.length > 0
                          ? `${primaryApiKey.tokenPrefix.substring(0, 20)}...`
                          : 'No API key available'}
                      </Typography>
                      {primaryApiKey && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleShowFullApiKey(primaryApiKey)}
                            sx={{ p: 0 }}
                          >
                            <Visibility sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => primaryApiKey && handleCopyToken(primaryApiKey.id)}
                            sx={{ p: 0 }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>Status:</Typography>
                    <Chip
                      label={primaryApiKey?.isActive ? 'Active' : 'Inactive'}
                      color={primaryApiKey?.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>Created:</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left' }}>
                      {formatTableDate(primaryApiKey?.createdAt, 'created')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>Last used:</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left' }}>{displayData.lastUsed}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>Usage today:</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left' }}>{displayData.usageToday} calls</Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Usage Details accordion */}
            <Accordion defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 1, mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChart sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Usage Details
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {/* Usage Details: two-column layout like Billing and Chat Service — label 0.750rem, value fontWeight 600 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, columnGap: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>API Calls</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', mb: 1.5, fontWeight: 600 }}>
                      {displayData.apiCalls.used.toLocaleString()} / {displayData.apiCalls.limit.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Reset date</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', fontWeight: 600 }}>{displayData.resetDate}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Tokens</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', fontWeight: 600 }}>
                      {displayData.tokens.used.toLocaleString()} / {displayData.tokens.limit.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Chat Service accordion */}
            <Accordion defaultExpanded sx={{ '&:before': { display: 'none' }, boxShadow: 1, mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Chat Service
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {/* Chat Service: same label/value layout as Billing — label 0.750rem, value fontWeight 600 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, columnGap: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Status</Typography>
                    <Box sx={{ mb: 1.5 }}>
                      <Chip
                        label={customerData.chatEnabled ? 'Enabled' : 'Disabled'}
                        color={customerData.chatEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Daily Limit</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', mb: 1.5, fontWeight: 600 }}>
                      {customerData.chatLimitDaily?.toLocaleString() || 'Unlimited'} messages
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Cost per Token</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', mb: 1.5, fontWeight: 600 }}>
                      ${customerData.chatCostPerToken || 0.000002}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.750rem' }}>Chat Tokens</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'left', fontWeight: 600 }}>
                      {apiKeys.filter(key => key.name.toLowerCase().includes('chat')).length} active
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>
      </Grid>

      {/* Delete Company Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Company"
        maxWidth="sm"
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCompany}
              variant="contained"
              color="error"
              disabled={deleting}
              sx={{ textTransform: 'none' }}
            >
              {deleting ? 'Deleting...' : 'Delete Company'}
            </Button>
          </Box>
        }
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{displayData.company}</strong>?
          </Typography>
                      <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action will permanently delete the company and all associated data including:
              </Typography>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>All admin users associated with this company</li>
                <li>All API keys associated with this company</li>
                <li>All usage data and billing information</li>
                <li>All user accounts and access</li>
                <li>All application integrations</li>
              </ul>
              <Typography variant="body2" sx={{ mt: 1 }}>
                This action cannot be undone.
              </Typography>
            </Alert>
        </Box>
      </Modal>

      {/* Full API Key Dialog */}
      <ApiKeyDialog
        open={showApiKeyDialog}
        onClose={() => setShowApiKeyDialog(false)}
        selectedKey={selectedApiKey}
        fullApiKey={fullApiKey}
        loadingFullKey={loadingFullKey}
        onCopyKey={(fullKey) => {
          navigator.clipboard.writeText(fullKey);
          showToast('API key copied to clipboard!', 'success');
        }}
        showCompany={false}
      />
    </Box>
  );
};

export default CustomerDetails; 